/**
 * Conexão com o servidor de partidas (item 6.1). Guarda as fotos que chegam e devolve o par para desenhar com
 * 100 ms de atraso, interpolando entre duas fotos (a predição do seu atleta é do item 6.2). Os comandos vão para o
 * servidor a cada tick.
 */
import { Client, type Room } from "@colyseus/sdk";
import { NET } from "@besenliga/shared";
import {
  MSG,
  Predictor,
  decodeSnapshot,
  type AthleteState,
  type AthleteMatchStats,
  type InputCommand,
  type PlayEvent,
  type RosterEntry,
  type Snapshot,
  type Welcome,
  type WorldState,
} from "@besenliga/sim";

/** Atraso de desenho: duas fotos de folga a 20 por segundo. */
export const INTERP_DELAY_MS = 100;
const BUFFER = 40;

/** Endereço do servidor: `?servidor=` no endereço, a variável do build ou o servidor local no desenvolvimento. */
export function serverUrl(): string {
  const asked = new URLSearchParams(location.hash.split("?")[1] ?? "").get("servidor");
  if (asked) return asked;
  const env = (import.meta.env as Record<string, string | undefined>).VITE_SERVIDOR;
  if (env) return env;
  return location.hostname === "localhost" || location.hostname === "127.0.0.1"
    ? `ws://${location.hostname}:2567`
    : "wss://besenliga-servidor.fly.dev";
}

export interface RemoteFrame {
  prev: WorldState;
  cur: WorldState;
  alpha: number;
}

/** Números da rede para o painel: ida e volta, última correção e comandos sem confirmação. */
export interface NetStats {
  ping: number | null;
  correction: number;
  pending: number;
  /** Latência simulada pedida no endereço (ms de ida e volta), se houver. */
  simulated: number;
}

/** O que a cena precisa de uma partida online. */
export interface RemoteSource {
  readonly you: string;
  readonly roster: readonly RosterEntry[];
  /** Muda quando alguém entra ou sai (para trocar a aparência da vaga). */
  readonly rosterVersion: number;
  frame(nowMs: number): RemoteFrame | null;
  latest(): WorldState | null;
  takePlays(): PlayEvent[];
  send(cmd: InputCommand): void;
  /** Seu atleta previsto (item 6.2): o tick anterior e o atual, para desenhar entre os dois. */
  predicted(): { prev: AthleteState; cur: AthleteState } | null;
  /** Correções desde a última chamada (previsto antes − previsto depois), para desfazer aos poucos. */
  takeCorrection(): { x: number; y: number; z: number };
  net(): NetStats;
}

/** Latência simulada (item 6.2): `?latencia=150` (ms de ida e volta) e `?jitter=20` (variação, ms). */
export function simulatedLatency(): { rtt: number; jitter: number } {
  const q = new URLSearchParams(location.hash.split("?")[1] ?? "");
  const n = (k: string) => {
    const v = Number(q.get(k));
    return Number.isFinite(v) && v > 0 ? Math.min(2000, v) : 0;
  };
  return { rtt: n("latencia"), jitter: n("jitter") };
}

/**
 * Atrasa mensagens como uma rede lenta, sem trocar a ordem (como o TCP do WebSocket): cada uma sai no instante
 * pedido ou logo depois da anterior.
 */
export class DelayLine {
  private last = 0;
  constructor(
    private readonly ms: number,
    private readonly jitter: number,
    private readonly random: () => number = Math.random,
  ) {}

  push(fn: () => void): void {
    if (this.ms <= 0 && this.jitter <= 0) return fn();
    const now = performance.now();
    const at = Math.max(this.last, now + this.ms + this.random() * this.jitter);
    this.last = at;
    setTimeout(fn, at - now);
  }
}

export interface JoinRequest {
  name: string;
  role: string;
  look: unknown;
  stats: Record<string, number>;
}

/** Interpola entre as fotos do buffer para o instante de servidor `t` (segundos). Exportado para teste. */
export function pickFrame(buffer: readonly { t: number; w: WorldState }[], t: number): RemoteFrame | null {
  const first = buffer[0];
  if (!first) return null;
  if (buffer.length === 1 || t <= first.t) return { prev: first.w, cur: first.w, alpha: 0 };
  for (let i = 1; i < buffer.length; i++) {
    const a = buffer[i - 1];
    const b = buffer[i];
    if (a && b && t <= b.t) return { prev: a.w, cur: b.w, alpha: (t - a.t) / Math.max(1e-6, b.t - a.t) };
  }
  // Sem foto nova a tempo: fica na última (sem extrapolar).
  const last = buffer[buffer.length - 1] ?? first;
  return { prev: last.w, cur: last.w, alpha: 0 };
}

export class Connection implements RemoteSource {
  you = "";
  roster: RosterEntry[] = [];
  rosterVersion = 0;
  results: Record<string, AthleteMatchStats> | null = null;
  private readonly buffer: { t: number; w: WorldState }[] = [];
  private plays: PlayEvent[] = [];
  /** Menor diferença (relógio local − relógio do servidor) já vista: o atraso da rede sem o "jitter". */
  private offset = Infinity;
  private closed = false;
  onChange: (() => void) | null = null;
  private predictor: Predictor | null = null;
  private readonly sentAt = new Map<number, number>();
  private ping: number | null = null;
  private correction = 0;
  private offset3 = { x: 0, y: 0, z: 0 };
  private readonly up: DelayLine;
  private readonly down: DelayLine;
  private readonly simulated: number;

  private constructor(private readonly room: Room) {
    const lat = simulatedLatency();
    this.simulated = lat.rtt;
    this.up = new DelayLine(lat.rtt / 2, lat.jitter / 2);
    this.down = new DelayLine(lat.rtt / 2, lat.jitter / 2);
    room.onMessage(MSG.welcome, (w: Welcome) =>
      this.down.push(() => {
        this.you = w.you;
        this.predictor = new Predictor(w.you);
        this.setRoster(w.roster);
      }),
    );
    room.onMessage(MSG.roster, (r: RosterEntry[]) => this.down.push(() => this.setRoster(r)));
    room.onMessage(MSG.snapshot, (s: Snapshot) => this.down.push(() => this.onSnapshot(s)));
    room.onMessage(MSG.result, (r: Record<string, AthleteMatchStats>) => {
      this.results = r;
      this.onChange?.();
    });
    room.onDrop((code: number, reason?: string) =>
      console.warn(`Conexão caiu (código ${code}${reason ? `: ${reason}` : ""})`),
    );
    room.onLeave((code: number, reason?: string) => {
      if (!this.closed) console.warn(`Saiu da partida (código ${code}${reason ? `: ${reason}` : ""})`);
      this.closed = true;
      this.onChange?.();
    });
  }

  static async join(url: string, req: JoinRequest): Promise<Connection> {
    const client = new Client(url);
    const room = await client.joinOrCreate("partida", req);
    return new Connection(room);
  }

  get isClosed(): boolean {
    return this.closed;
  }

  get ready(): boolean {
    return this.you !== "" && this.buffer.length > 0;
  }

  private setRoster(r: RosterEntry[]): void {
    this.roster = r;
    this.rosterVersion++;
    this.onChange?.();
  }

  private onSnapshot(s: Snapshot): void {
    if (!this.roster.length) return;
    const w = decodeSnapshot(s, this.roster);
    const t = s.tick / NET.simTickHz;
    this.offset = Math.min(this.offset, performance.now() / 1000 - t);
    this.buffer.push({ t, w });
    if (this.buffer.length > BUFFER) this.buffer.shift();
    this.plays.push(...w.plays);
    // Reconciliação do seu atleta com o que o servidor confirmou.
    const i = this.roster.findIndex((r) => r.id === this.you);
    const ack = s.acks?.[i];
    const mine = w.athletes[i];
    if (this.predictor && mine && ack !== undefined) {
      const r = this.predictor.reconcile(mine, ack);
      this.correction = r.error;
      this.offset3 = {
        x: this.offset3.x + r.delta.x,
        y: this.offset3.y + r.delta.y,
        z: this.offset3.z + r.delta.z,
      };
      const sent = this.sentAt.get(ack);
      if (sent !== undefined) {
        const rtt = performance.now() - sent;
        this.ping = this.ping === null ? rtt : this.ping * 0.8 + rtt * 0.2;
      }
      for (const k of this.sentAt.keys()) if (k <= ack) this.sentAt.delete(k);
    }
    // Partida nova na sala (o tick volta a zero): esquece as fotos da anterior.
    const prev = this.buffer[this.buffer.length - 2];
    if (prev && prev.t > t) {
      this.buffer.splice(0, this.buffer.length - 1);
      this.offset = performance.now() / 1000 - t;
      this.results = null;
    }
  }

  frame(nowMs: number): RemoteFrame | null {
    return pickFrame(this.buffer, nowMs / 1000 - this.offset - INTERP_DELAY_MS / 1000);
  }

  latest(): WorldState | null {
    return this.buffer[this.buffer.length - 1]?.w ?? null;
  }

  takePlays(): PlayEvent[] {
    const p = this.plays;
    this.plays = [];
    return p;
  }

  send(cmd: InputCommand): void {
    if (this.closed) return;
    const phase = this.latest()?.match?.phase;
    const frozen = phase === "inicio" || phase === "gol" || phase === "fim";
    const numbered = this.predictor ? this.predictor.input(cmd, frozen) : { ...cmd, seq: 0 };
    this.sentAt.set(numbered.seq, performance.now());
    this.up.push(() => {
      if (!this.closed) this.room.send(MSG.command, numbered);
    });
  }

  predicted(): { prev: AthleteState; cur: AthleteState } | null {
    const p = this.predictor;
    if (!p?.predicted) return null;
    return { prev: p.previous ?? p.predicted, cur: p.predicted };
  }

  takeCorrection(): { x: number; y: number; z: number } {
    const o = this.offset3;
    this.offset3 = { x: 0, y: 0, z: 0 };
    return o;
  }

  net(): NetStats {
    return {
      ping: this.ping,
      correction: this.correction,
      pending: this.predictor?.unacknowledged ?? 0,
      simulated: this.simulated,
    };
  }

  async leave(): Promise<void> {
    this.closed = true;
    await this.room.leave();
  }
}
